import {useState, useRef, useCallback, useEffect} from 'react';
import {useChekinSDK} from 'context/ChekinSDK';

export interface ServerDetectionResponse {
  type: 'detection' | 'error';
  data: {
    is_face_detected: boolean;
    direction: string;
    is_blinking: boolean;
    counter: number;
    ear: null;
    move_percent: number | null;
  };
}

export interface UseLivenessWebSocketProps {
  onMessage?: (response: ServerDetectionResponse) => void;
  autoConnect?: boolean;
}

export const useLivenessWebSocket = ({
  onMessage,
  autoConnect = true,
}: UseLivenessWebSocketProps = {}) => {
  const {connectLivenessWebSocket} = useChekinSDK();
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const socketRef = useRef<WebSocket | null>(null);

  const connect = useCallback(() => {
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    if (socketRef.current) {
      socketRef.current.close();
    }

    try {
      setIsConnecting(true);
      setError(null);

      const socket = connectLivenessWebSocket();
      socketRef.current = socket;

      socket.onopen = () => {
        console.log('WebSocket connected to liveness detection server');
        setIsConnected(true);
        setIsConnecting(false);
      };

      socket.onclose = () => {
        console.log('WebSocket disconnected from liveness detection server');
        setIsConnected(false);
      };

      socket.onerror = event => {
        console.error('WebSocket error:', event);
        setIsConnected(false);
        setIsConnecting(false);
        setError(new Error('WebSocket connection error'));
      };

      socket.onmessage = event => {
        try {
          const response = JSON.parse(event.data) as ServerDetectionResponse;
          if (onMessage) {
            onMessage(response);
          }
        } catch (err) {
          console.error('Error processing WebSocket message:', err);
        }
      };
    } catch (err) {
      console.error('Failed to create WebSocket connection:', err);
      setIsConnecting(false);
      setError(err instanceof Error ? err : new Error('Unknown WebSocket error'));
    }
  }, [onMessage, connectLivenessWebSocket]);

  const disconnect = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.close();
      socketRef.current = null;
      setIsConnected(false);
    }
  }, []);

  const sendData = useCallback((data: ArrayBuffer) => {
    if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
      return false;
    }

    try {
      socketRef.current.send(data);
      return true;
    } catch (err) {
      console.error('Error sending data over WebSocket:', err);
      return false;
    }
  }, []);

  useEffect(() => {
    if (autoConnect) {
      connect();
    }
  }, [autoConnect, connect]);

  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  return {
    isConnected,
    isConnecting,
    error,
    connect,
    disconnect,
    sendData,
  };
};
